import { Badge } from "@risalabs_frontend_org/oasis-ui-kit";
import { useState } from "react";
import { DenialQueryReasonBucket } from "../../../../../data-model/denialQueryBucket";
import ChevronDown from "../../../../../svg/chevron-down";
import Reasons from "./Reasons";

const DENIAL_QUERY_BUCKET_CLASSNAME = "denial-query-bucket";

/** Props for the {@link DenialQueryBucket} component. */
interface DenialQueryBucketProps {
  /** Title displayed in the summary header. */
  title?: string;
  /** Number of columns used for denial and query sections. Defaults to two. */
  numberOfColumns?: number;
  /** Accent color for denial-related borders, badges, and reason markers. */
  accentColor?: string;
  /** Light background color for the summary header. */
  headerBackgroundColor?: string;
  /** Complete text displayed in the monthly-denial badge. */
  monthlyDenialText?: string;
  /** Complete text displayed in the total-denial badge. */
  totalDenialText?: string;
  /** Denial reasons for the regimen. */
  denialReasons?: DenialQueryReasonBucket[];
  /** Query reasons for the regimen. */
  queryReasons?: DenialQueryReasonBucket[];
  /** Whether the denial bucket is greater than zero. */
  isDenialBucketGreaterThanZero?: boolean;
  /** Whether the query bucket is greater than zero. */
  isQueryBucketGreaterThanZero?: boolean;
}

const ACCENT_COLORS = {
  DENIAL: "#CC0300",
  QUERY: "#0056D6",
};

/**
 * Displays denial-query summary badges and expandable regimen details.
 *
 * @param props - Optional layout and color configuration for the summary.
 * @returns The denial-query bucket UI.
 */
const DenialQueryBucket = ({
  title = "Denial Reasons and Queries for this Regimen",
  numberOfColumns = 2,
  accentColor = "#CC0300",
  headerBackgroundColor = "#FFF1F2",
  monthlyDenialText = "Monthly Denial - NA",
  totalDenialText = "Total Denial - NA",
  denialReasons = [],
  queryReasons = [],
  isDenialBucketGreaterThanZero = false,
  isQueryBucketGreaterThanZero = false,
}: DenialQueryBucketProps) => {
  const [isContentVisible, setIsContentVisible] = useState(true);

  /**
   * Toggles visibility of the denial-query detail content.
   *
   * @returns Nothing.
   */
  const toggleContentVisibility = (): void => {
    setIsContentVisible((isVisible) => !isVisible);
  };

  return (
    <div
      className={`${DENIAL_QUERY_BUCKET_CLASSNAME}__container overflow-hidden rounded-md border`}
      style={{ borderColor: accentColor }}
    >
      <div
        className={`${DENIAL_QUERY_BUCKET_CLASSNAME}__header flex items-center justify-between p-4`}
        style={{ backgroundColor: headerBackgroundColor }}
      >
        <div
          className={`${DENIAL_QUERY_BUCKET_CLASSNAME}__header-title flex items-center gap-[0.62rem]`}
        >
          <div
            className={`${DENIAL_QUERY_BUCKET_CLASSNAME}__header-title-border h-[1.375rem] w-[0.25rem] rounded-sm`}
            style={{ backgroundColor: accentColor }}
          >
            &nbsp;
          </div>
          <div
            className={`${DENIAL_QUERY_BUCKET_CLASSNAME}__header-title-text text-small font-bold text-primaryGray-1`}
          >
            {title}
          </div>
        </div>
        <div
          className={`${DENIAL_QUERY_BUCKET_CLASSNAME}__header-counter-details flex items-center gap-2`}
        >
          <div>
            <Badge
              size="small"
              text={monthlyDenialText}
              backgroundColor={"#CC0300"}
              textColor="#FFFFFF"
              className={`${DENIAL_QUERY_BUCKET_CLASSNAME}__header-counter-badge`}
              borderColor="transparent"
            />
          </div>
          <div>
            <Badge
              size="small"
              text={totalDenialText}
              backgroundColor="#0056D6"
              textColor="#FFFFFF"
              className={`${DENIAL_QUERY_BUCKET_CLASSNAME}__header-counter-badge`}
              borderColor="transparent"
            />
          </div>
          <button
            type="button"
            className={`${DENIAL_QUERY_BUCKET_CLASSNAME}__header-toggle flex items-center`}
            aria-expanded={isContentVisible}
            aria-controls={`${DENIAL_QUERY_BUCKET_CLASSNAME}__content`}
            aria-label={
              isContentVisible
                ? "Hide denial reasons and queries"
                : "Show denial reasons and queries"
            }
            onClick={toggleContentVisibility}
          >
            <ChevronDown rotate={isContentVisible ? 180 : 0} />
          </button>
        </div>
      </div>
      <div
        id={`${DENIAL_QUERY_BUCKET_CLASSNAME}__content`}
        className={`${DENIAL_QUERY_BUCKET_CLASSNAME}__content-wrapper ${
          isContentVisible
            ? `${DENIAL_QUERY_BUCKET_CLASSNAME}__content-wrapper--visible`
            : ""
        }`}
        aria-hidden={!isContentVisible}
      >
        <div
          className={`${DENIAL_QUERY_BUCKET_CLASSNAME}__content grid max-h-[35rem] overflow-auto ${isContentVisible ? "p-4" : "p-0"}`}
          style={{
            gridTemplateColumns: `repeat(${numberOfColumns}, minmax(0, 1fr))`,
          }}
        >
          {isDenialBucketGreaterThanZero && (
            <div className="col-span-1">
              <Reasons
                heading="Denial Reasons"
                textColor={ACCENT_COLORS.DENIAL}
                numberOfColumns={numberOfColumns > 1 ? 1 : 2}
                reasons={denialReasons}
              />
            </div>
          )}
          {isQueryBucketGreaterThanZero && (
            <div className="col-span-1">
              <Reasons
                heading="Query Reasons"
                textColor={ACCENT_COLORS.QUERY}
                numberOfColumns={numberOfColumns > 1 ? 1 : 2}
                reasons={queryReasons}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
export default DenialQueryBucket;
